/**
 * Brief 3.6 Phase D9 — Public marketing-page Nerea widget.
 *
 * Floating chat bubble bottom-right on every marketing route. Anonymous,
 * per-IP rate-limited by the backend. Multi-turn history is held in
 * component state — nothing persisted client-side.
 */
import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2, AlertTriangle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BACKEND_URL}/api/public/nerea/chat`;

export default function PublicNereaWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async () => {
    const q = draft.trim();
    if (!q || sending) return;
    setError(""); setSending(true);
    const next = [...messages, { role: "user", content: q }];
    setMessages(next);
    setDraft("");
    try {
      const history = next
        .slice(0, -1)
        .slice(-10)
        .map(({ role, content }) => ({ role, content }));
      const { data } = await axios.post(ENDPOINT, {
        query: q,
        history,
      });
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: data.content,
          abstained: data.abstained,
          citations: data.citations,
        },
      ]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        setError(typeof detail === "string" ? detail : "Hit the rate limit — try again in a moment.");
      } else {
        setError("Nerea couldn't answer just now. Try again, or contact sales.");
      }
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid="public-nerea-launcher"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 rounded-full bg-[#00E5FF] text-black hover:bg-[#3df4ff] px-4 py-2.5 text-[13px] font-medium shadow-lg shadow-cyan-500/20"
      >
        <Sparkles className="h-4 w-4" />
        Ask Nerea
      </button>
    );
  }

  return (
    <div
      data-testid="public-nerea-widget"
      className="fixed bottom-5 right-5 z-50 w-[360px] h-[520px] rounded-2xl border border-white/10 bg-[#0a0a0a] shadow-2xl shadow-black/50 flex flex-col overflow-hidden"
    >
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-[#00E5FF]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">NeptunePay</div>
            <div className="text-[13px] text-white font-medium">Ask Nerea</div>
          </div>
        </div>
        <button
          type="button"
          data-testid="public-nerea-close"
          onClick={() => setOpen(false)}
          className="text-zinc-400 hover:text-white p-1"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5">
        {messages.length === 0 && (
          <div data-testid="public-nerea-empty" className="text-[12.5px] text-zinc-400 leading-relaxed">
            Ask anything about NeptunePay — payment methods, integration,
            chargebacks, settlement. I won't know account-specific things until
            you sign up.
          </div>
        )}
        {messages.map((m, i) => (
          <PublicBubble key={i} m={m} />
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="public-nerea-typing">
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-zinc-400 text-[12px] inline-flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" /> thinking…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      {error && (
        <div data-testid="public-nerea-error" className="px-4 pb-1 text-[11.5px] text-rose-300 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> {error}
        </div>
      )}
      <div className="border-t border-white/10 px-3 py-2.5 flex items-end gap-2">
        <textarea
          data-testid="public-nerea-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          placeholder="Ask a question…"
          rows={1}
          maxLength={1000}
          className="flex-1 resize-none rounded-md border border-white/[0.08] bg-white/[0.02] px-3 py-1.5 text-[13px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#00E5FF]/40 max-h-[100px]"
        />
        <button
          type="button"
          data-testid="public-nerea-send"
          onClick={send}
          disabled={sending || !draft.trim()}
          className="inline-flex items-center justify-center rounded-full bg-[#00E5FF] text-black hover:bg-[#3df4ff] disabled:bg-zinc-700 disabled:text-zinc-400 transition-colors h-8 w-8 shrink-0"
        >
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}

function PublicBubble({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={[
          "max-w-[88%] rounded-2xl px-3 py-2 text-[12.5px] leading-relaxed whitespace-pre-wrap",
          isUser
            ? "bg-white text-black rounded-br-md"
            : m.abstained
              ? "border border-amber-400/30 bg-amber-400/[0.06] text-amber-100 rounded-bl-md"
              : "border border-white/[0.08] bg-white/[0.04] text-zinc-100 rounded-bl-md",
        ].join(" ")}
      >
        {m.content}
        {m.citations && m.citations.length > 0 && (
          <div className="mt-1.5 pt-1.5 border-t border-white/10 space-y-0.5">
            {m.citations.slice(0, 3).map((c, i) => (
              <div key={i} className="text-[10.5px] text-zinc-400">
                <span className="text-[#00E5FF]">↗</span> {c.source_title || c.source || "neptune"}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
